import React, { useEffect, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useOnClickOutside } from 'usehooks-ts';
import { DevTool } from '@hookform/devtools';
import { useDateFilterForm, DateFilterFieldNames } from 'hooks/forms';
import { useQueryDateRange } from 'hooks/routes';
import {
  formatDateRangeString,
  formatInputDate,
  parseInputDate,
  dateTransform,
} from 'utils';
import { ChevronDownIcon_24 } from 'assets/icons';
import { RangeCalendar } from 'components/Calendar';
import Field from 'components/Field';
import Button from 'components/Button';

import { compareDates, getDefaultRange } from './TableDateFilter.utils';
import messages from './TableDateFilter.messages';
import styles from './TableDateFilter.module.css';

const TableDateFilter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const [appliedRange, setAppliedRange] = useState<DateRange | undefined>(
    getDefaultRange(),
  );
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(getDefaultRange());
  const [queryRange, setQueryRange] = useQueryDateRange();

  const form = useDateFilterForm();

  useEffect(() => {
    if ((!queryRange.from || !queryRange.to) && appliedRange) {
      setQueryRange(appliedRange);
    }

    if (
      queryRange.from &&
      queryRange.to &&
      appliedRange?.from &&
      appliedRange.to &&
      (!compareDates(queryRange.from, appliedRange.from) ||
        !compareDates(queryRange.to, appliedRange.to))
    ) {
      setAppliedRange(queryRange);
    }
  }, [queryRange, setQueryRange]);

  const onFormChange = async () => {
    if (await form.trigger()) {
      const from = parseInputDate(form.getValues(DateFilterFieldNames.startDate));
      const to = parseInputDate(form.getValues(DateFilterFieldNames.endDate));

      setDraftRange({
        from,
        to,
      });
    }
  };

  const onCalendarChange = (date: DateRange | undefined) => {
    setDraftRange(date);
    form.setValue(
      DateFilterFieldNames.startDate,
      (date?.from ? formatInputDate(date.from).replace(/[^\d]/g, '') : '') as never,
    );
    form.setValue(
      DateFilterFieldNames.endDate,
      (date?.to ? formatInputDate(date.to).replace(/[^\d]/g, '') : '') as never,
    );
    form.clearErrors();
  };

  const onOpen = () => {
    setDraftRange(appliedRange);
    appliedRange?.from &&
      form.setValue(
        DateFilterFieldNames.startDate,
        formatInputDate(appliedRange.from).replace(/[^\d]/g, '') as never,
      );
    appliedRange?.to &&
      form.setValue(
        DateFilterFieldNames.endDate,
        formatInputDate(appliedRange.to).replace(/[^\d]/g, '') as never,
      );

    setIsOpen(true);
  };

  const onApply = () => {
    setIsOpen(false);
    setAppliedRange(draftRange);
    draftRange && setQueryRange(draftRange);
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  useOnClickOutside(ref, onCancel);

  return (
    <div className={styles.wrapper} ref={ref}>
      <span className={styles.clickable} onClick={isOpen ? onCancel : onOpen}>
        <span className={styles.caption}>{messages.label} </span>
        {!!appliedRange?.from && !!appliedRange?.to && (
          <>
            <span className={styles.period}>
              {formatDateRangeString(appliedRange.from, appliedRange.to)}
            </span>
            <span className={styles.chevron}>
              <ChevronDownIcon_24 />
            </span>
          </>
        )}
      </span>

      {isOpen && (
        <div className={styles.calendarWrapper}>
          <form onChange={onFormChange} onSubmit={form.handleSubmit(onApply)}>
            <DevTool control={form.control} />

            <div className={styles.dateInputsWrapper}>
              <div>
                <span className={styles.dateInputLabel}>{messages.startDate}</span>
                <Field
                  form={form}
                  name={DateFilterFieldNames.startDate}
                  className={styles.dateInput}
                  labelClassname={styles.dateInputInnerLabel}
                  type={'text'}
                  maxLength={10}
                  {...dateTransform}
                />
              </div>

              <div>
                <span className={styles.dateInputLabel}>{messages.endDate}</span>
                <Field
                  form={form}
                  name={DateFilterFieldNames.endDate}
                  className={styles.dateInput}
                  labelClassname={styles.dateInputInnerLabel}
                  type={'text'}
                  maxLength={10}
                  {...dateTransform}
                />
              </div>
            </div>
          </form>

          <RangeCalendar range={draftRange} setRange={onCalendarChange} />

          <div className={styles.buttonsWrapper}>
            <Button onClick={onCancel} variant={'outline'}>
              {messages.cancelButton}
            </Button>
            <Button
              disabled={!draftRange?.from || !draftRange.to}
              onClick={form.handleSubmit(onApply)}
            >
              {messages.applyButton}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableDateFilter;
